import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import service from '../../services/http';
import { FetchLoadingStateEnum } from '../../constants';

export interface ExperimentState {
  isRunning: boolean;
  runIndex: number;
  isCancelled: boolean;
  experimentId: number | null;
  hasRunExperiment: boolean;
  loading: { submitTestingMetrics?: FetchLoadingStateEnum };
}

export interface EvaluatedPromptResponse {
  itpExecutionId: string;
  documentIdentifier: string;
  promptIdentifier: string;
  matchContent: string;
  matchFound: boolean;
  experimentId: number;
  createdAt: string;
}

const initialState: ExperimentState = {
  isRunning: false,
  runIndex: 0,
  isCancelled: false,
  experimentId: null,
  hasRunExperiment: false,
  loading: {}
};

export const submitTestingMetrics = createAsyncThunk<void, EvaluatedPromptResponse[], { rejectValue: Error }>(
  'v1/analyze-report/testing-metrics',
  async (results, { rejectWithValue }) => {
    try {
      await service.request<{ data: EvaluatedPromptResponse }>({
        method: 'post',
        apiUrlKey: 'itpUrl',
        relativePath: `/v1/analyze-report/testing-metrics`,
        data: results,
        headers: { 'Content-Type': 'application/json' }
      });

      return undefined;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  }
);

export const experimentSlice = createSlice({
  name: 'experiment',
  initialState,
  reducers: {
    setIsRunning: (state, action: PayloadAction<boolean>) => {
      state.isRunning = action.payload;
    },
    setRunIndex: (state, action: PayloadAction<number>) => {
      state.runIndex = action.payload;
    },
    setIsCancelled: (state, action: PayloadAction<boolean>) => {
      state.isCancelled = action.payload;
    },
    setExperimentId: (state, action: PayloadAction<number | null>) => {
      state.experimentId = action.payload;
    },
    resetExperiment: (state) => {
      state.isRunning = false;
      state.hasRunExperiment = false;
      state.runIndex = 0;
      state.isCancelled = false;
      state.experimentId = null;
      state.loading = {};
    }
  },

  extraReducers(builder) {
    builder
      .addCase(submitTestingMetrics.pending, (state) => {
        state.loading.submitTestingMetrics = FetchLoadingStateEnum.loading;
      })
      .addCase(submitTestingMetrics.fulfilled, (state) => {
        state.loading.submitTestingMetrics = FetchLoadingStateEnum.fulfilled;
        state.hasRunExperiment = true;
      })
      .addCase(submitTestingMetrics.rejected, (state) => {
        state.loading.submitTestingMetrics = FetchLoadingStateEnum.error;
      });
  }
});

export const {
  setIsRunning,
  setRunIndex,
  setIsCancelled,
  setExperimentId,
  resetExperiment
} = experimentSlice.actions;

export default experimentSlice.reducer;
